import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { MessageService } from '@openng/optimus-ui/api';
import { Optimus } from '@openng/optimus-ui/config';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { GraphQlService } from '~ngx-shared/graph-ql';
import { LayoutConfig, LayoutService, LoadingService, MenuService } from '~ngx-shared/layout';
import { FileService, VersionService } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { UserService } from '~shared/services';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';
import { FarabiService } from './services/farabi.service';
import { Community } from './theme/community';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly versionService = inject(VersionService);
  private readonly messageService = inject(MessageService);
  private readonly farabiService = inject(FarabiService);
  private readonly optimusConfig = inject(Optimus);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly graphQlService = inject(GraphQlService);

  constructor() {
    const authenticationService = inject(AuthenticationService);
    const userService = inject(UserService);
    const fileService = inject(FileService);

    fileService.init(
      `${environment.externalServiceUrl}/file/upload`,
      `${environment.externalServiceUrl}/file`
    );

    effect(() => {
      const isNewAvailable = this.versionService.isNewVersionAvailable();
      if (isNewAvailable) {
        this.messageService.add({
          severity: 'error',
          summary: 'update',
          closable: false,
          sticky: true,
          key: 'new_version_available'
        });
      }
    });

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        authenticationService.init(
          ['default-roles-iggoe', 'iggoe-user'],
          url =>
            url.startsWith(environment.hasuraUrl) || url.startsWith(environment.externalServiceUrl)
        );
        this.authorizationService.setAllowedRoles(
          Object.values(AuthorizationUtil.GROUP_COMMUNITY_ALL)
        );
        userService.init();
      }
    });
  }

  ngOnInit(): void {
    this.loadingService.startLoading();

    //optional configuration with the default configuration
    const config: LayoutConfig = {
      preset: 'Aura',
      primary: 'emerald',
      surface: null,
      darkTheme: false,
      menuMode: 'static'
    };
    this.layoutService.layoutConfig.set(config);
    this.optimusConfig.theme.set({
      ...this.optimusConfig.theme(),
      preset: Community
    });

    this.translocoService.selectTranslateObject('primeng').subscribe(translated => {
      this.optimusConfig.setTranslation(translated);
    });

    this.menuService.sideBarItems$.next([
      {
        label: 'Home',
        items: [
          {
            label: 'dashboard',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/', 'dashboard']
          },
          {
            label: 'profile',
            icon: 'pi pi-fw pi-user',
            routerLink: ['/', 'profile']
          }
        ]
      },
      {
        label: 'shared.communities',
        items: [
          {
            label: 'shared.community_type.religious_communities',
            routerLink: ['/', 'religious-communities'],
            icon: 'pi pi-fw pi-building-columns',
            state: { permissions: AuthorizationUtil.GROUP_RELIGIOUS }
          },
          {
            label: 'shared.community_type.cultic_communities',
            routerLink: ['/', 'cultic-communities'],
            icon: 'pi pi-fw pi-building-columns',
            items: [
              {
                label: 'shared.community_type.mosque_facilities',
                routerLink: ['/', 'mosque-facilities'],
                icon: 'pi pi-fw pi-building-columns'
              }
            ]
          },
          {
            label: 'shared.community_type.mosques',
            routerLink: ['/', 'mosques'],
            icon: 'pi pi-fw pi-building-columns'
          },
          {
            label: 'shared.community_type.professional_associations',
            routerLink: ['/', 'professional-associations'],
            icon: 'pi pi-fw pi-building-columns'
          },
          {
            label: 'shared.community_type.prayer_rooms',
            routerLink: ['/', 'prayer-rooms'],
            icon: 'pi pi-fw pi-building-columns',
            state: { permissions: AuthorizationUtil.GROUP_RELIGIOUS }
          },
          {
            label: 'shared.communities',
            routerLink: ['/', 'communities'],
            icon: 'pi pi-fw pi-building-columns'
          },
          {
            label: 'community.deadlines',
            routerLink: ['/', 'deadlines'],
            icon: 'pi pi-fw pi-clock',
            state: { permissions: AuthorizationUtil.GROUP_COMMUNITY_MANAGEMENT }
          }
        ]
      },
      {
        label: 'shared.protocols',
        items: [
          {
            label: 'shared.meeting_minutes_religious_communities',
            icon: 'pi pi-fw pi-file-edit',
            routerLink: ['/', 'protocols', 'religious-communities']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_RELIGIOUS }
      },
      {
        label: 'shared.communication_planning',
        items: [
          // {
          //   label: 'shared.announcements',
          //   icon: 'pi pi-fw pi-megaphone',
          //   routerLink: ['/', 'announcements'],
          //   state: { permissions: AuthorizationUtil.GROUP_RELIGIOUS }
          // },
          // {
          //   label: 'shared.messages',
          //   icon: 'pi pi-inbox',
          //   routerLink: ['/', 'messages'],
          // },
          {
            label: 'shared.events',
            icon: 'pi pi-calendar-clock',
            routerLink: ['/', 'events']
          },
          {
            label: 'community.tom',
            icon: 'pi pi-calendar-clock',
            routerLink: ['/', 'tom']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_COMMUNITY_MANAGEMENT }
      },
      {
        label: 'shared.actors',
        items: [
          {
            label: 'shared.role.community_responsible',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'community-responsible']
          },
          {
            label: 'shared.role.community_religious',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'community-religious']
          },
          {
            label: 'shared.role.community_offices',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'community-offices'],
            state: { permissions: RoleEnum.MODERATOR }
          },
          {
            label: 'shared.people',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'actors', 'people']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_OFFICE }
      },
      {
        id: 'farabi',
        label: 'farabi',
        visible: false,
        items: [
          {
            label: 'shared.termination_of_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'farabi', 'termination-of-services']
          },
          {
            label: 'shared.service_contracts',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'farabi', 'service-contracts']
          },
          {
            label: 'shared.igaza_certificates',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'farabi', 'igaza-certificates']
          },
          {
            label: 'shared.declaration_of_commitment_il_pd.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'farabi', 'declaration-of-commitment-il-pds']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_OFFICE }
      },
      {
        label: 'shared.administration',
        items: [
          {
            label: 'shared.communities',
            icon: 'pi pi-fw pi-building-columns',
            routerLink: ['/', 'administration', 'communities']
          },
          {
            label: 'shared.actors',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'administration', 'actors']
          }
          // {
          //   label: 'settings',
          //   icon: 'pi pi-fw pi-cog',
          //   routerLink: ['/', 'settings']
          // }
        ],
        state: { permissions: AuthorizationUtil.GROUP_OFFICE }
      }
    ]);

    this.farabiService.init();

    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(role => {
        if (role) {
          this.graphQlService.init(() => role);
        }
      });

    this.loadingService.stopLoading();
  }
}
